import {
  type AmountV1CurrencyEnum,
  type OfferShippingsV1,
  type PimProductV11,
  type PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'

import { shippingAccessorGetMinFreeShippingThreshold } from '../../../../shipping'
import { productAccessorIsFreeShipping } from '../../../model/productAccessor/productAccessorIsFreeShipping'
import { type PRODUCT_TEMPLATES } from '../../../model/productTemplates'
import { useProductPageContext } from '../../productPageContext'

import { useProductPrices } from './useProductPrices'

interface ProductDeliveryPrices {
  freeShippingThreshold: {
    amount: number | null | undefined
    currency: AmountV1CurrencyEnum
  }
  isFreeShippingSae: boolean
  shippingPriceMarketplace?: number
}

interface OfferDeliveryCostsV1WithMandatoryTotalShippingPrice
  extends OfferShippingsV1 {
  totalShippingPrice: number
}

const getMarketplaceCheapestDeliveryCost = (
  offerDeliveryCosts: OfferShippingsV1[] | null,
): number | undefined => {
  if (!offerDeliveryCosts) {
    return undefined
  }

  const definedOfferDeliveryCosts =
    offerDeliveryCosts.filter<OfferDeliveryCostsV1WithMandatoryTotalShippingPrice>(
      (
        element,
      ): element is OfferDeliveryCostsV1WithMandatoryTotalShippingPrice =>
        typeof element.totalShippingPrice === 'number',
    )

  // return minimum totalShippingPrice of definedOfferDeliveryCosts
  return Math.min(
    ...definedOfferDeliveryCosts.map(
      (offerDeliveryCost) => offerDeliveryCost.totalShippingPrice,
    ),
  )
}

export const useProductDeliveryPrices = ({
  currentPrescriptionType,
  offerId,
  product,
  productTemplate,
}: {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  offerId: string | null
  product: PimProductV11
  productTemplate: PRODUCT_TEMPLATES
}): ProductDeliveryPrices => {
  const { displayPrice } = useProductPrices({
    currentPrescriptionType,
    offerId,
    product,
  })
  const {
    shipping: { marketplaceDeliveryCosts, saeShippingCosts },
  } = useProductPageContext()

  const freeShippingThreshold = shippingAccessorGetMinFreeShippingThreshold(
    saeShippingCosts.carriers,
  )

  return {
    freeShippingThreshold,
    isFreeShippingSae: productAccessorIsFreeShipping({
      currentPrescriptionType,
      displayPrice,
      freeShippingThreshold,
      productTemplate,
    }),
    shippingPriceMarketplace: getMarketplaceCheapestDeliveryCost(
      marketplaceDeliveryCosts,
    ),
  }
}
